import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import { unwrapResult } from '@reduxjs/toolkit'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { offeringTagCreateThunk, selectAvailableTags, tagListThunk, selectCurrentTempleOfferingTags, offeringTagListThunk } from 'store/reducers/temple/offeringTagSlice'
import { IOfferingResponse } from 'types/api/temple'
import { extractError } from 'utils/api'

interface BulkOfferingTagDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const BulkOfferingTagDialog: React.FC<BulkOfferingTagDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const availableTags = useAppSelector(selectAvailableTags)
    const currentOfferingTags = useAppSelector(selectCurrentTempleOfferingTags)
    const [selectedTag, setSelectedTag] = useState<string>('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (open) {
            setSelectedTag('')
            if (availableTags.length === 0) {
                dispatch(tagListThunk({ tag_type: 'offering' }))
            }
            dispatch(offeringTagListThunk({}))
        }
    }, [open, availableTags.length, dispatch])

    const handleSave = async () => {
        if (offerings.length === 0 || !selectedTag) return

        setSubmitting(true)
        try {
            const offeringsToCreate = offerings.filter(offering =>
                !currentOfferingTags.some(tagRelation =>
                    tagRelation.tag === selectedTag &&
                    (tagRelation.offering === offering.url || String(tagRelation.offering) === String(offering.id))
                )
            )

            if (offeringsToCreate.length === 0) {
                enqueueSnackbar('Selected offerings already have this tag', { variant: 'info' })
                onClose()
                return
            }

            const results = await Promise.allSettled(
                offeringsToCreate.map(offering =>
                    dispatch(offeringTagCreateThunk({ offering: offering.url, tag: selectedTag })).then(unwrapResult)
                )
            )

            const successCount = results.filter(r => r.status === 'fulfilled').length
            const failCount = results.filter(r => r.status === 'rejected').length

            if (successCount > 0) {
                enqueueSnackbar(`Assigned tag to ${successCount} offerings`, { variant: 'success' })
            }
            if (failCount > 0) {
                enqueueSnackbar(`Failed to assign tag to ${failCount} offerings`, { variant: 'error' })
            }

            onComplete?.()
            onClose()
        } catch (error) {
            enqueueSnackbar(extractError(error) ?? 'Failed to update selected offerings', { variant: 'error' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Bulk Assign Tag</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Apply one tag to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}. Duplicate assignments will be skipped.
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                    <TextField
                        select
                        label="Select Tag"
                        value={selectedTag}
                        onChange={(e) => setSelectedTag(e.target.value)}
                        variant="outlined"
                        fullWidth
                    >
                        {availableTags.map(tag => (
                            <MenuItem key={tag.url} value={tag.url}>
                                {tag.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined" disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={submitting || offerings.length === 0 || !selectedTag}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BulkOfferingTagDialog
